import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://biedradar.nl";

interface SeoProps {
  /** Page title; " - Biedradar" is appended automatically. */
  title: string;
  /** Meta description (≤ 155 chars recommended). */
  description: string;
  /**
   * Canonical path override. Defaults to the current pathname, which is
   * correct for SSR/prerender (useLocation resolves per route). Pass an
   * explicit path for aliases (e.g. /prijzen → canonical /pricing).
   */
  canonicalPath?: string;
  /** noindex this page (e.g. app/account pages). Default: indexable. */
  noindex?: boolean;
}

/**
 * Per-page <head> for content/landing pages that don't use GuideLayout.
 * Emits a unique title, description, self-referential canonical, and Open
 * Graph / Twitter tags so every indexable page is distinct (no duplicate
 * homepage title in the sitemap). Articles use GuideLayout instead.
 */
export function Seo({ title, description, canonicalPath, noindex }: SeoProps) {
  const path = canonicalPath ?? useLocation().pathname;
  const canonical = `${SITE_URL}${path}`;
  const fullTitle = `${title} - Biedradar`;
  const ogImage = `${SITE_URL}/og-image.png`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Biedradar" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
